import React from 'react';
import { piktoAufloesen } from '../../services/mangelKatalog/piktoRegister';

/**
 * Piktogramm eines Katalogeintrags („Standardmangel per Torte“, Paket B):
 * ISO-7010-Zeichen oder Lucide-Symbol laut piktoRegister.ts, in einem Kasten
 * der Größe `groesse` (px). Ohne oder mit unbekanntem Schlüssel erscheint das
 * allgemeine Warnzeichen (W001).
 *
 * Der Kasten selbst trägt weder Farbe noch Rand oder Schatten — das entscheidet
 * der Aufrufer über `className`.
 */
export interface KatalogPiktoProps {
    schluessel?: string | null;
    /** Kantenlänge in px, Standard 24. */
    groesse?: number;
    className?: string;
    /** Überschreibt den Namen für Screenreader (sonst der Titel aus dem Register). */
    titel?: string;
}

const KASTEN = 'inline-flex shrink-0 items-center justify-center align-middle';

export const KatalogPikto: React.FC<KatalogPiktoProps> = ({ schluessel, groesse = 24, className, titel }) => {
    const def = piktoAufloesen(schluessel);
    const name = titel ?? def.titel;
    const masse: React.CSSProperties = { width: groesse, height: groesse };
    const klassen = (zusatz: string): string => [KASTEN, zusatz, className].filter(Boolean).join(' ');

    if (def.art === 'iso') {
        // dangerouslySetInnerHTML ist hier vertretbar: def.svg stammt ausschließlich
        // aus den statischen, bereinigten Konstanten in piktoSvgs.ts (kein Nutzer-
        // oder Servertext; ohne Skript, Ereignis-Attribute und externe Verweise —
        // piktoRegister.test.ts prüft das). Inline statt <img>, damit kein
        // Netzabruf und keine data:-URL nötig ist. Das SVG hat kein width/height;
        // es füllt den Kasten, die viewBox hält das Seitenverhältnis.
        return (
            <span
                role="img"
                aria-label={name}
                className={klassen('[&>svg]:block [&>svg]:h-full [&>svg]:w-full')}
                style={masse}
                dangerouslySetInnerHTML={{ __html: def.svg }}
            />
        );
    }

    const Icon = def.icon;
    return (
        <span role="img" aria-label={name} className={klassen(def.farbe)} style={masse}>
            <Icon
                size={groesse}
                aria-hidden="true"
                focusable="false"
                style={def.drehung === undefined ? undefined : { transform: `rotate(${def.drehung}deg)` }}
            />
        </span>
    );
};
